import { FunctionComponent } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import CreditCardIcon from 'dibs-icons/exports/legacy/CreditCard';
import PayPalBadge from 'dibs-icons/exports/legacy/PaymentPaypal';
import KlarnaBadge from 'dibs-icons/exports/legacy/KlarnaBadgeFilled';
import BankAccountIcon from 'dibs-icons/exports/legacy/BankAccount';
import ApplePayBadge from 'dibs-icons/exports/legacy/ApplePay';
import { TABS } from '../constants/checkoutConstants';

import styles from './styles/CheckoutPaymentStepOption.scss';

type PaymentMethod = (typeof TABS)[keyof typeof TABS];

type CheckoutPaymentStepOptionProps = {
    paymentMethod: PaymentMethod;
    hasSavedCreditCards: boolean;
};

export const CheckoutPaymentStepOption: FunctionComponent<
    CheckoutPaymentStepOptionProps
> = props => {
    const { paymentMethod, hasSavedCreditCards } = props;

    return (
        <div className={styles.option}>
            <CheckoutPaymentStepOptionTitle
                paymentMethod={paymentMethod}
                hasSavedCreditCards={hasSavedCreditCards}
            />
            <CheckoutPaymentStepOptionIcon paymentMethod={paymentMethod} />
        </div>
    );
};

const CheckoutPaymentStepOptionTitle: FunctionComponent<CheckoutPaymentStepOptionProps> = props => {
    const { paymentMethod, hasSavedCreditCards } = props;

    if (paymentMethod === TABS.TAB_CREDIT_CARD && !hasSavedCreditCards) {
        return (
            <FormattedMessage
                id="checkout.CheckoutPaymentStepOption.credit_card"
                defaultMessage="Credit/Debit Card"
            />
        );
    } else if (paymentMethod === TABS.TAB_CREDIT_CARD && hasSavedCreditCards) {
        return (
            <FormattedMessage
                id="checkout.CheckoutPaymentStepOption.new_credit_card"
                defaultMessage="New Credit/Debit Card"
            />
        );
    } else if (paymentMethod === TABS.TAB_PAYPAL_ACCOUNT) {
        return (
            <FormattedMessage
                id="checkout.CheckoutPaymentStepOption.paypal"
                defaultMessage="PayPal"
            />
        );
    } else if (paymentMethod === TABS.TAB_KLARNA) {
        return (
            <FormattedMessage
                id="checkout.CheckoutPaymentStepOption.klarna"
                defaultMessage="Klarna"
            />
        );
    } else if (paymentMethod === TABS.TAB_APPLE_PAY) {
        return (
            <FormattedMessage
                id="checkout.CheckoutPaymentStepOption.apple_pay"
                defaultMessage="Apple Pay"
            />
        );
    } else if (paymentMethod === TABS.TAB_BANK_ACCOUNT) {
        return (
            <FormattedMessage
                id="checkout.CheckoutPaymentStepOption.bank_account"
                defaultMessage="Bank Account"
            />
        );
    } else if (paymentMethod === TABS.TAB_WIRE_TRANSFER) {
        return (
            <FormattedMessage
                id="checkout.CheckoutPaymentStepOption.wire_transfer"
                defaultMessage="Wire Transfer"
            />
        );
    } else if (paymentMethod === TABS.TAB_CHECK) {
        return (
            <FormattedMessage
                id="checkout.CheckoutPaymentStepOption.check"
                defaultMessage="Check"
            />
        );
    } else {
        return null;
    }
};

type CheckoutPaymentStepOptionIconProps = {
    paymentMethod: PaymentMethod;
};

const CheckoutPaymentStepOptionIcon: FunctionComponent<
    CheckoutPaymentStepOptionIconProps
> = props => {
    const { paymentMethod } = props;

    switch (paymentMethod) {
        case TABS.TAB_CREDIT_CARD:
            return <CreditCardIcon className={styles.icon} />;
        case TABS.TAB_PAYPAL_ACCOUNT:
            return <PayPalBadge className={styles.icon} />;
        case TABS.TAB_KLARNA:
            return <KlarnaBadge className={styles.icon} />;
        case TABS.TAB_APPLE_PAY:
            return <ApplePayBadge className={styles.icon} />;
        case TABS.TAB_BANK_ACCOUNT:
            return <BankAccountIcon className={styles.icon} />;
        default:
            return null;
    }
};
